import { Loader2, RefreshCw } from "lucide-react";
import { Navigate, Outlet } from "react-router";

import { Button } from "@/components/ui/button";
import { isApiError, useMe } from "@/lib/api";

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
    </div>
  );
}

/**
 * Shown when the session check failed for a non-auth reason (backend down,
 * network blip). Redirecting to /login here would log users out during an
 * outage, so offer a retry instead.
 */
function SessionCheckFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <p className="text-muted-foreground text-sm">
        Can&apos;t reach the server right now. Your session is unaffected.
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </Button>
    </div>
  );
}

/** True when the error means "not logged in", as opposed to "request failed". */
function isAuthError(error: unknown): boolean {
  return isApiError(error) && (error.status === 401 || error.status === 403);
}

/**
 * Gates authenticated routes. While `useMe()` loads, show a spinner; on an
 * auth error (401/403) or missing session, redirect to /login; on transport
 * or server errors, show a retry state instead of logging the user out.
 * Otherwise render the nested routes with the resolved profile available via
 * Outlet context.
 *
 * Real enforcement is the backend (401/403) — this is the client-side gate
 * that keeps the UI honest and redirects unauthenticated users.
 */
export function ProtectedRoute() {
  const { data: me, isLoading, error, refetch } = useMe();

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (error && !isAuthError(error)) {
    return <SessionCheckFailed onRetry={() => void refetch()} />;
  }

  if (error || !me) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet context={{ me }} />;
}

/**
 * Nested admin gate. Assumes it lives inside ProtectedRoute (so `me` is
 * already loaded); non-admins are bounced to /home. The backend remains the
 * authoritative guard for admin endpoints.
 */
export function AdminRoute() {
  const { data: me, isLoading, error, refetch } = useMe();

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (error && !isAuthError(error)) {
    return <SessionCheckFailed onRetry={() => void refetch()} />;
  }

  if (!me) {
    return <Navigate to="/login" replace />;
  }

  if (me.role !== "admin") {
    return <Navigate to="/home" replace />;
  }

  return <Outlet />;
}

/**
 * Inverse gate for the public auth pages (login, signup, …). If a valid
 * session already exists, bounce to /home so logged-in users don't see the
 * login form. While loading, show a spinner to avoid a flash of the form.
 * On errors (auth or transport) the form renders — a guest page is always a
 * safe fallback.
 */
export function GuestRoute() {
  const { data: me, isLoading } = useMe();

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  if (me) {
    return <Navigate to="/home" replace />;
  }

  return <Outlet />;
}
